/**
 * ViewportMinimap — small overlay showing a 1x thumbnail of the current slice
 * with a live rectangle indicating the visible region.
 *
 * The thumbnail is rendered from cached pixel data using the same approach as
 * useSeriesThumbnails / FindingThumbnails — proven to produce correct brightness.
 * No camera resets, no viewport manipulation.
 *
 * Only visible when zoom > 100%.
 */

import React, { useRef, useEffect, useCallback, useState } from "react";
import { getCornerstone, findHangingProtocolViewport } from "../../utils/dicomLoader";
import { SHARED_RENDERING_ENGINE_ID } from "../../utils/renderingEngine";
import { logger } from "@/utils/logger";

// Cornerstone's getCurrentImageIdIndex throws when the volume actor isn't
// loaded yet (e.g. during drag-drop series swap). Treat any throw as
// "viewport not ready" and skip the minimap update for this frame.
function safeGetCurrentSlice(vp: any): number | null {
  try {
    const idx = vp.getCurrentImageIdIndex?.();
    return typeof idx === "number" ? idx : null;
  } catch (err) {
    logger.debug("[ViewportMinimap] getCurrentImageIdIndex skipped:", err);
    return null;
  }
}

const MINIMAP_ZOOM_THRESHOLD = 1.05;

interface ViewportMinimapProps {
  slotId: string;
  zoom: number;
  /** Window center (level) from the viewport */
  windowCenter: number;
  /** Window width from the viewport */
  windowWidth: number;
  size?: number;
}

export const ViewportMinimap: React.FC<ViewportMinimapProps> = ({
  slotId,
  zoom,
  windowCenter,
  windowWidth,
  size = 100,
}) => {
  const thumbnailCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const overlayCanvasRef = useRef<HTMLCanvasElement>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const capturedKeyRef = useRef<string | null>(null);
  const drawDimsRef = useRef({ drawW: size, drawH: size, drawX: 0, drawY: 0, vpW: 1, vpH: 1 });
  const [, setCameraRevision] = useState(0);

  // Render a 1x thumbnail from cached pixel data + current W/L.
  // Uses the same pixel→canvas approach as useSeriesThumbnails (proven correct).
  const captureThumbnail = useCallback(() => {
    const vp = findHangingProtocolViewport(slotId);
    if (!vp) return;

    const currentSlice = safeGetCurrentSlice(vp);
    if (currentSlice === null) return;
    const cacheKey = `${currentSlice}_${windowCenter}_${windowWidth}`;
    if (cacheKey === capturedKeyRef.current && thumbnailCanvasRef.current) return;

    const imageId = vp.getCurrentImageId?.();
    if (!imageId) return;

    let cornerstone: any;
    try {
      cornerstone = getCornerstone();
    } catch {
      return;
    }

    const image = cornerstone.cache?.getImage?.(imageId);
    if (!image) return;

    const columns = image.columns ?? image.width;
    const rows = image.rows ?? image.height;
    if (!columns || !rows) return;

    const pixelData = image.getPixelData?.();
    if (!pixelData) return;

    capturedKeyRef.current = cacheKey;

    // Compute aspect-correct draw dimensions
    const aspect = columns / rows;
    let drawW = size;
    let drawH = size;
    let drawX = 0;
    let drawY = 0;
    if (aspect > 1) {
      drawH = size / aspect;
      drawY = (size - drawH) / 2;
    } else {
      drawW = size * aspect;
      drawX = (size - drawW) / 2;
    }

    const vpCanvas = vp.getCanvas?.() as HTMLCanvasElement | undefined;
    const vpW = vpCanvas?.width ?? columns;
    const vpH = vpCanvas?.height ?? rows;
    drawDimsRef.current = { drawW, drawH, drawX, drawY, vpW, vpH };

    // Render pixel data to a temp full-res canvas, then scale down.
    // Pixel data from Cornerstone cache is already rescaled — no slope/intercept needed.
    // (Matches useSeriesThumbnails.renderToCanvas pattern.)
    const lower = windowCenter - windowWidth / 2;
    const upper = windowCenter + windowWidth / 2;

    const tempCanvas = document.createElement("canvas");
    tempCanvas.width = columns;
    tempCanvas.height = rows;
    const tempCtx = tempCanvas.getContext("2d");
    if (!tempCtx) return;

    const imgData = tempCtx.createImageData(columns, rows);
    for (let i = 0; i < pixelData.length; i++) {
      const val = pixelData[i] as number;
      const clamped = Math.max(lower, Math.min(upper, val));
      const normalized = ((clamped - lower) / (upper - lower)) * 255;
      imgData.data[i * 4] = normalized;
      imgData.data[i * 4 + 1] = normalized;
      imgData.data[i * 4 + 2] = normalized;
      imgData.data[i * 4 + 3] = 255;
    }
    tempCtx.putImageData(imgData, 0, 0);

    // Scale to minimap
    if (!thumbnailCanvasRef.current) {
      thumbnailCanvasRef.current = document.createElement("canvas");
      thumbnailCanvasRef.current.width = size;
      thumbnailCanvasRef.current.height = size;
    }
    const ctx = thumbnailCanvasRef.current.getContext("2d");
    if (!ctx) return;
    ctx.clearRect(0, 0, size, size);
    ctx.drawImage(tempCanvas, drawX, drawY, drawW, drawH);
  }, [slotId, size, windowCenter, windowWidth]);

  // Draw the overlay: static thumbnail + live rectangle
  const drawOverlay = useCallback(() => {
    const overlay = overlayCanvasRef.current;
    if (!overlay) return;
    const ctx = overlay.getContext("2d");
    if (!ctx) return;

    ctx.clearRect(0, 0, size, size);

    // Ensure we have a thumbnail
    if (!thumbnailCanvasRef.current || capturedKeyRef.current === null) {
      captureThumbnail();
    }

    // Check if slice or W/L changed
    const vp = findHangingProtocolViewport(slotId);
    if (vp) {
      const currentSlice = safeGetCurrentSlice(vp);
      if (currentSlice !== null) {
        const cacheKey = `${currentSlice}_${windowCenter}_${windowWidth}`;
        if (cacheKey !== capturedKeyRef.current) {
          captureThumbnail();
        }
      }
    }

    // Draw the static thumbnail
    if (thumbnailCanvasRef.current) {
      ctx.drawImage(thumbnailCanvasRef.current, 0, 0);
    }

    // Draw visible-region rectangle
    if (!vp) return;

    const currentZoom = vp.getZoom?.() ?? 1;
    if (currentZoom <= MINIMAP_ZOOM_THRESHOLD) return;

    const pan = vp.getPan?.() ?? [0, 0];
    const { drawW, drawH, drawX, drawY } = drawDimsRef.current;

    // Pan values are in CSS pixels relative to the rendered image area (letterboxed),
    // not the full viewport element. Compute image CSS dims from aspect ratio.
    const vpElement = vp.element as HTMLElement | undefined;
    const elW = vpElement?.clientWidth ?? 1;
    const elH = vpElement?.clientHeight ?? 1;
    const imgAspect = drawW / drawH;
    const imgCssW = Math.min(elW, elH * imgAspect);
    const imgCssH = Math.min(elH, elW / imgAspect);

    const visW = drawW / currentZoom;
    const visH = drawH / currentZoom;
    const cx = 0.5 - pan[0] / (imgCssW * currentZoom);
    const cy = 0.5 - pan[1] / (imgCssH * currentZoom);
    const visCenterX = drawX + cx * drawW;
    const visCenterY = drawY + cy * drawH;

    ctx.strokeStyle = "rgba(67, 97, 238, 0.9)";
    ctx.lineWidth = 1.5;
    ctx.strokeRect(visCenterX - visW / 2, visCenterY - visH / 2, visW, visH);
  }, [slotId, size, captureThumbnail, windowCenter, windowWidth]);

  // Listen to camera changes — update rectangle position
  useEffect(() => {
    const vp = findHangingProtocolViewport(slotId);
    const element = vp?.element;
    if (!element) {
      drawOverlay();
      return;
    }

    drawOverlay();

    const onCameraModified = () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        drawOverlay();
        setCameraRevision((r) => r + 1);
      }, 30);
    };

    element.addEventListener("CORNERSTONE_CAMERA_MODIFIED", onCameraModified);
    return () => {
      element.removeEventListener("CORNERSTONE_CAMERA_MODIFIED", onCameraModified);
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [slotId, drawOverlay]);

  // Re-render thumbnail when W/L changes
  useEffect(() => {
    captureThumbnail();
    drawOverlay();
  }, [windowCenter, windowWidth, captureThumbnail, drawOverlay]);

  if (zoom <= MINIMAP_ZOOM_THRESHOLD) return null;

  return (
    <div
      className="absolute bottom-14 right-2 z-30 rounded overflow-hidden border border-border-strong shadow-lg"
      style={{ width: size, height: size, background: "rgba(0,0,0,0.7)" }}
    >
      <canvas
        ref={overlayCanvasRef}
        width={size}
        height={size}
        className="pointer-events-auto cursor-crosshair"
        onClick={(e) => {
          const canvas = overlayCanvasRef.current;
          if (!canvas) return;
          const vp = findHangingProtocolViewport(slotId);
          if (!vp) return;
          const rect = canvas.getBoundingClientRect();
          const clickX = e.clientX - rect.left;
          const clickY = e.clientY - rect.top;
          const { drawW, drawH, drawX, drawY } = drawDimsRef.current;
          const vpElement = vp.element as HTMLElement | undefined;
          const elW = vpElement?.clientWidth ?? 1;
          const elH = vpElement?.clientHeight ?? 1;
          // Clamp click to image area (ignore letterbox bands)
          const clampedX = Math.max(drawX, Math.min(clickX, drawX + drawW));
          const clampedY = Math.max(drawY, Math.min(clickY, drawY + drawH));
          // Convert minimap click to fractional offset from center, then to CSS pixels
          const fracX = (clampedX - drawX - drawW / 2) / drawW;
          const fracY = (clampedY - drawY - drawH / 2) / drawH;
          const imgAspect = drawW / drawH;
          const imgCssW = Math.min(elW, elH * imgAspect);
          const imgCssH = Math.min(elH, elW / imgAspect);
          const currentZoom = vp.getZoom?.() ?? 1;
          vp.setPan([-fracX * imgCssW * currentZoom, -fracY * imgCssH * currentZoom]);
          try {
            const cornerstone = getCornerstone();
            const engine = cornerstone.getRenderingEngine(SHARED_RENDERING_ENGINE_ID);
            engine?.renderViewports([vp.id]);
          } catch {
            /* ignore */
          }
        }}
      />
    </div>
  );
};

export default ViewportMinimap;
